import React, { useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  MessageCircle,
  Minimize2,
  Paperclip,
  Send,
  X,
} from 'lucide-react';
import { legalOperationsService } from '../services/legalOperationsService';

const DEFAULT_PALETTE = {
  navy: '#0F2742',
  accent: '#1F8A70',
  accentSoft: '#E8F5F2',
  page: '#F7F9FB',
  border: '#DCE7E3',
  textSoft: '#6B7280',
};

const LABOR_PALETTE = {
  navy: '#1F3B64',
  accent: '#A27849',
  accentSoft: '#F5EBDD',
  page: '#FBF8F2',
  border: '#E8DCC8',
  textSoft: '#635f58',
};
const LABOR_CHAT_SESSION_KEY = 'wazin_labor_chat_conversation_v1';
const LABOR_CASES_STORAGE_KEY = 'wazin_labor_cases_v2';
const LABOR_DRAFT_STORAGE_KEY = 'wazin_labor_intake_draft_v2';

const getLaborClientContext = (location) => {
  try {
    const params = new URLSearchParams(location.search);
    const referenceNumber = params.get('ref') || params.get('referenceNumber');
    const cases = JSON.parse(localStorage.getItem(LABOR_CASES_STORAGE_KEY) || '[]');
    const matchedCase = referenceNumber
      ? cases.find((item) => item.referenceNumber === referenceNumber)
      : null;
    const draft = JSON.parse(localStorage.getItem(LABOR_DRAFT_STORAGE_KEY) || 'null');
    const client = matchedCase?.client || draft?.client || {};
    return {
      client_name: client.fullName || [client.firstName, client.fatherName, client.familyName].filter(Boolean).join(' ') || undefined,
      client_phone: client.mobile || undefined,
      client_email: client.email || undefined,
      reference_number: matchedCase?.referenceNumber || referenceNumber || undefined,
      source_path: `${location.pathname}${location.search}`,
    };
  } catch (error) {
    return { source_path: `${location.pathname}${location.search}` };
  }
};

const getCaseContext = (pathname) => {
  const match = pathname.match(/\/client\/cases?\/([^/]+)|\/client\/case\/([^/]+)/);
  const caseId = match?.[1] || match?.[2];

  if (!caseId) {
    return null;
  }

  return `WZ-${String(caseId).toUpperCase()}`;
};

const LiveChatWidget = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isArabic = i18n.language !== 'en';
  const isLaborClientPage =
    location.pathname.startsWith('/labor-intake') ||
    location.pathname.startsWith('/client/labor-case');
  const isLaborOperationsPage =
    location.pathname.startsWith('/admin/labor-intake') ||
    location.pathname.startsWith('/cases/');
  const isLaborPortalPage = isLaborClientPage || isLaborOperationsPage;

  const palette = useMemo(
    () => (isLaborPortalPage ? LABOR_PALETTE : DEFAULT_PALETTE),
    [isLaborPortalPage]
  );

  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [message, setMessage] = useState('');
  const [fileName, setFileName] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [conversationId, setConversationId] = useState(() => localStorage.getItem(LABOR_CHAT_SESSION_KEY) || '');
  const [messages, setMessages] = useState([
    {
      id: 1,
      role: 'assistant',
      text: isArabic
        ? 'مرحباً بك في محادثة وازن. اكتب رسالتك وسيتابعها فريقنا المختص.'
        : 'Welcome to Wazin chat. Send your message and our team will review it.',
      time: isArabic ? 'الآن' : 'Now',
    },
  ]);

  const fileInputRef = useRef(null);
  const caseContext = useMemo(() => getCaseContext(location.pathname), [location.pathname]);

  const handleToggle = () => {
    if (isLaborOperationsPage) {
      navigate('/admin/labor-intake/messages');
      return;
    }
    setIsOpen((prev) => !prev);
    setIsMinimized(false);
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    setFileName(file?.name || '');
  };

  const handleSend = async () => {
    const trimmed = message.trim();

    if (!trimmed && !fileName) {
      return;
    }

    const userMessage = {
      id: Date.now(),
      role: 'user',
      text: trimmed || (isArabic ? `تم إرفاق ملف: ${fileName}` : `Attached file: ${fileName}`),
      time: isArabic ? 'الآن' : 'Now',
    };

    setMessages((prev) => [...prev, userMessage]);
    setMessage('');
    setIsSending(true);

    if (isLaborClientPage) {
      try {
        const result = await legalOperationsService.sendIncomingMessage({
          ...getLaborClientContext(location),
          conversation_id: conversationId || undefined,
          message: userMessage.text,
        });
        if (result.conversationId) {
          setConversationId(result.conversationId);
          localStorage.setItem(LABOR_CHAT_SESSION_KEY, result.conversationId);
        }
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now() + 1,
            role: 'assistant',
            text: isArabic
              ? 'تم استلام رسالتك وإشعار فريق وازن لمتابعتها.'
              : 'Your message has been received and the Wazin team has been notified.',
            time: isArabic ? 'الآن' : 'Now',
          },
        ]);
      } catch (error) {
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now() + 1,
            role: 'assistant',
            text: isArabic
              ? 'تعذر إرسال الرسالة حالياً. يرجى المحاولة مرة أخرى أو التواصل عبر البريد labor.support@wazn.sa.'
              : 'The message could not be sent now. Please try again or email labor.support@wazn.sa.',
            time: isArabic ? 'الآن' : 'Now',
          },
        ]);
      } finally {
        setIsSending(false);
        setFileName('');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
      return;
    }

    window.setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          role: 'assistant',
          text: isArabic
            ? 'تم استلام رسالتك تجريبياً، وسيتم ربط الإرسال الفعلي في المرحلة التالية.'
            : 'Your message has been received in demo mode. Full delivery will be connected later.',
          time: isArabic ? 'الآن' : 'Now',
        },
      ]);
      setIsSending(false);
      setFileName('');

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }, 900);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleToggle}
        aria-label={isArabic ? 'المحادثة الفورية' : 'Live chat'}
        style={{
          position: 'fixed',
          left: 22,
          bottom: 22,
          width: 58,
          height: 58,
          borderRadius: '50%',
          border: 'none',
          background: isLaborPortalPage
            ? `linear-gradient(135deg, ${palette.navy}, ${palette.accent})`
            : `linear-gradient(135deg, ${palette.accent}, #146A56)`,
          color: '#FFFFFF',
          boxShadow: '0 20px 35px rgba(15, 39, 66, 0.22)',
          zIndex: 1000,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <MessageCircle size={24} />
      </button>

      {isOpen && (
        <div
          style={{
            position: 'fixed',
            left: 22,
            bottom: 92,
            width: 'min(380px, calc(100vw - 32px))',
            background: '#FFFFFF',
            border: `1px solid ${palette.border}`,
            borderRadius: 24,
            boxShadow: '0 28px 50px rgba(15, 39, 66, 0.2)',
            overflow: 'hidden',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: isLaborPortalPage
                ? `linear-gradient(135deg, ${palette.navy}, #31527d)`
                : `linear-gradient(135deg, ${palette.navy}, #1A436C)`,
              color: '#FFFFFF',
              padding: '16px 18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 14,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <strong style={{ fontSize: 16, fontWeight: 900 }}>
                {isArabic ? 'محادثة وازن الفورية' : 'Wazin Live Chat'}
              </strong>
              <span style={{ fontSize: 12, opacity: 0.88 }}>
                {caseContext
                  ? (isArabic ? `مرتبطة بالقضية ${caseContext}` : `Linked to case ${caseContext}`)
                  : (isArabic ? 'تواصل سريع مع فريق وازن' : 'Quick contact with the Wazin team')}
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                type="button"
                onClick={() => setIsMinimized((prev) => !prev)}
                title={isArabic ? 'تصغير' : 'Minimize'}
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 12,
                  border: '1px solid rgba(255,255,255,0.14)',
                  background: 'rgba(255,255,255,0.12)',
                  color: '#FFFFFF',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Minimize2 size={16} />
              </button>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                title={isArabic ? 'إغلاق' : 'Close'}
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 12,
                  border: '1px solid rgba(255,255,255,0.14)',
                  background: 'rgba(255,255,255,0.12)',
                  color: '#FFFFFF',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              <div
                style={{
                  padding: '18px 18px 12px',
                  background: palette.page,
                  borderBottom: `1px solid ${palette.border}`,
                }}
              >
                <div
                  style={{
                    background: '#FFFFFF',
                    border: `1px solid ${palette.border}`,
                    borderRadius: 18,
                    padding: '14px 16px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                  }}
                >
                  <span
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 14,
                      background: palette.accentSoft,
                      color: palette.accent,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <FileText size={18} />
                  </span>
                  <div>
                    <div style={{ color: palette.navy, fontWeight: 900, marginBottom: 4 }}>
                      {isArabic ? 'مرحباً بك' : 'Welcome'}
                    </div>
                    <div style={{ color: palette.textSoft, fontSize: 13, lineHeight: 1.8 }}>
                      {isArabic
                        ? 'أرسل استفسارك أو ملاحظتك، ويمكنك إرفاق ملف عند الحاجة.'
                        : 'Send your question or note, and attach a file if needed.'}
                    </div>
                  </div>
                </div>
              </div>

              <div
                style={{
                  maxHeight: 280,
                  overflowY: 'auto',
                  padding: 16,
                  background: '#FFFFFF',
                  display: 'grid',
                  gap: 10,
                }}
              >
                {messages.map((item) => {
                  const isUser = item.role === 'user';

                  return (
                    <div
                      key={item.id}
                      style={{
                        display: 'flex',
                        justifyContent: isUser ? 'flex-start' : 'flex-end',
                      }}
                    >
                      <div
                        style={{
                          maxWidth: '84%',
                          padding: '12px 14px',
                          borderRadius: 18,
                          background: isUser ? palette.accentSoft : '#F8FAFC',
                          border: `1px solid ${palette.border}`,
                          color: palette.navy,
                          fontSize: 13,
                          lineHeight: 1.8,
                        }}
                      >
                        <div>{item.text}</div>
                        <div
                          style={{
                            marginTop: 6,
                            fontSize: 11,
                            color: palette.textSoft,
                          }}
                        >
                          {item.time}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {isSending && (
                  <div style={{ color: palette.textSoft, fontSize: 12 }}>
                    {isArabic ? 'جارٍ إرسال الرسالة...' : 'Sending message...'}
                  </div>
                )}
              </div>

              <div
                style={{
                  borderTop: `1px solid ${palette.border}`,
                  padding: 14,
                  background: '#FFFFFF',
                }}
              >
                <textarea
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  rows={3}
                  placeholder={isArabic ? 'اكتب رسالتك هنا...' : 'Write your message here...'}
                  style={{
                    width: '100%',
                    resize: 'none',
                    borderRadius: 16,
                    border: `1px solid ${palette.border}`,
                    padding: '12px 14px',
                    fontFamily: 'inherit',
                    fontSize: 13,
                    outline: 'none',
                    background: palette.page,
                    marginBottom: 10,
                  }}
                />

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 10,
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      hidden
                      onChange={handleFileChange}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      style={{
                        border: `1px solid ${palette.border}`,
                        background: '#FFFFFF',
                        color: palette.navy,
                        borderRadius: 14,
                        padding: '10px 12px',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        fontWeight: 700,
                      }}
                    >
                      <Paperclip size={16} />
                      {isArabic ? 'إرفاق ملف' : 'Attach file'}
                    </button>

                    {fileName && (
                      <span style={{ color: palette.textSoft, fontSize: 12 }}>{fileName}</span>
                    )}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span
                      style={{
                        fontSize: 11,
                        color: palette.textSoft,
                        background: palette.page,
                        border: `1px solid ${palette.border}`,
                        borderRadius: 999,
                        padding: '6px 10px',
                      }}
                    >
                      {isArabic ? 'وضع تجريبي' : 'Demo mode'}
                    </span>
                    <button
                      type="button"
                      onClick={handleSend}
                      disabled={isSending}
                      style={{
                        border: 'none',
                        background: isLaborPortalPage
                          ? `linear-gradient(135deg, ${palette.navy}, ${palette.accent})`
                          : `linear-gradient(135deg, ${palette.accent}, #146A56)`,
                        color: '#FFFFFF',
                        borderRadius: 14,
                        padding: '11px 16px',
                        cursor: isSending ? 'wait' : 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        fontWeight: 800,
                      }}
                    >
                      <Send size={15} />
                      {isArabic ? 'إرسال' : 'Send'}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default LiveChatWidget;
